import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useGeri, useDuzenlemeBitti } from '@/lib/geri';
import api from '@/lib/api';
import { useProducts } from '@/contexts/ProductsContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft, Save, Sparkles, Check, Plus, X, ArrowLeftRight, Pencil } from 'lucide-react';
import { toast } from 'sonner';

const types = [
  { value: 'raw_material', label: 'Hammadde' },
  { value: 'semi_finished', label: 'Yarı Mamul' },
  { value: 'finished', label: 'Mamul' },
  { value: 'consumable', label: 'Sarf Malzeme' },
  { value: 'fixed_asset', label: 'Demirbaş' },
  { value: 'guest_consumption', label: 'Misafir Tüketim' },
  { value: 'trade_goods', label: 'Ticari Mal' },
  { value: 'other', label: 'Diğer' },
];
const groupLabels = { weight: 'Agirlik', volume: 'Hacim', area: 'Alan', length: 'Uzunluk', quantity: 'Miktar' };

export default function ProductForm() {
  const { refreshProducts } = useProducts();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  // Geri = önceki sayfa; önceki adım yoksa eski liste adresi (bkz. lib/geri.js)
  const geri = useGeri('/erp/products');
  const duzenlemeBitti = useDuzenlemeBitti();
  const isEdit = !!id;
  const returnTo = searchParams.get('return');
  const prefillName = searchParams.get('prefill_name') || '';
  const [categories, setCategories] = useState([]);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savedProduct, setSavedProduct] = useState(null);
  const [suggestion, setSuggestion] = useState(null);
  const [suggestLoading, setSuggestLoading] = useState(false);
  const suggestTimer = useRef(null);
  const [form, setForm] = useState({
    code: '', name: prefillName, category_id: '', subcategory_id: '', sinif_id: '', type: 'raw_material',
    unit: 'kg', unit_id: '', barcode: '', min_stock_level: '', kdv_rate: '',
    description: '',
    unit_conversions: []
  });
  const [codeSuggested, setCodeSuggested] = useState(false);
  // Kod alanının altında gösterilen bilgi: bu TÜRDE en son hangi kod kullanıldı.
  // Kullanıcı isteği (2026-09-20): "en son girilen ürün kodunu bilmiyoruz".
  const [kodBilgi, setKodBilgi] = useState(null);
  // Yeni üründe kod alanı KİLİTLİ gelir: sıradaki boş kod zaten yazılıyor,
  // elle yazmak mükerrer koda ve yanlış aileye yol açıyordu (kullanıcı,
  // 2026-09-20: "ürün kodu girmek tam bir bela"). Düzenleme kipinde kilit yok.
  const [kodKilitli, setKodKilitli] = useState(true);

  useEffect(() => {
    api.get('/categories').then(r => setCategories(r.data)).catch(() => {});
    api.get('/units').then(r => setUnits(r.data)).catch(() => {});
    if (isEdit) {
      api.get(`/products/${id}`).then(r => {
        const p = r.data;
        setForm({
          code: p.code || '', name: p.name || '',
          category_id: p.category_id || '', subcategory_id: p.subcategory_id || '', sinif_id: p.sinif_id || '',
          type: p.type || 'raw_material',
          unit: p.unit || 'kg', unit_id: p.unit_id || '',
          barcode: p.barcode || '', min_stock_level: p.min_stock_level ?? '', kdv_rate: p.kdv_rate ?? '',
          description: p.description || '',
          unit_conversions: (p.unit_conversions || []).map(c => ({
            from_unit_id: c.from_unit_id || '',
            to_unit_id:   c.to_unit_id   || '',
            factor:       c.factor != null ? String(c.factor) : ''
          }))
        });
      }).catch(() => toast.error('Urun yuklenemedi'));
    }
  }, [id, isEdit]);

  // Group units by unit_group
  const unitsByGroup = {};
  units.forEach(u => {
    if (!unitsByGroup[u.unit_group]) unitsByGroup[u.unit_group] = [];
    unitsByGroup[u.unit_group].push(u);
  });

  const parentCategories = categories.filter(c => !c.parent_id);
  const subcategories = categories.filter(c => c.parent_id === form.category_id);
  // Üçüncü seviye "Masraf Grubu": alt kategorinin altındaki sınıf (inşaatta
  // poz listesinin MASRAF kodu). Kategori ağacında açılabiliyordu ama kartta
  // seçilemiyordu.
  const siniflar = categories.filter(c => form.subcategory_id && c.parent_id === form.subcategory_id);
  const katAdi = (c) => (c.kod ? `${c.kod} · ${c.name}` : c.name);

  const updateField = useCallback((field, value) => {
    setForm(prev => {
      const next = { ...prev, [field]: value };
      if (field === 'category_id') { next.subcategory_id = ''; next.sinif_id = ''; }
      if (field === 'subcategory_id') next.sinif_id = '';
      if (field === 'unit_id') {
        const u = units.find(x => x.id === value);
        if (u) next.unit = u.abbreviation;
        // Update all conversion to_unit_id to new main unit
        next.unit_conversions = (next.unit_conversions || []).map(c => ({ ...c, to_unit_id: value }));
      }
      return next;
    });
  }, [units]);

  // Kod önerisi TÜRE bağlı (kod ailesi türe ait, kategoriye değil — sunucu
  // tarafında ölçüldü: kategori alt kümesinden türetilen öneri dolu kodlara
  // çarpıp mükerrer kod döndürüyordu). Kategori değişince yeniden sorulmaz.
  useEffect(() => {
    if (isEdit) return;
    if (!form.type) return;

    const suggestCode = async () => {
      try {
        const res = await api.post('/products/suggest-code', { type: form.type });
        setKodBilgi(res.data);
        if (res.data.suggested_code) {
          setForm(prev => {
            // Elle yazılmış kodu EZME; yalnızca boşsa ya da öneriyi tutuyorsa değiştir
            if (prev.code && prev.code.trim() !== '' && !codeSuggested) return prev;
            return { ...prev, code: res.data.suggested_code };
          });
          setCodeSuggested(true);
        }
      } catch (err) {
        console.error('Code suggestion error:', err);
      }
    };

    suggestCode();
  }, [form.type, isEdit]);

  // Clear suggested flag when user manually edits code
  const handleCodeChange = (value) => {
    setCodeSuggested(false);
    updateField('code', value);
  };

  // AI suggestion
  const handleNameChange = useCallback((value) => {
    updateField('name', value);
    setSuggestion(null);
    if (suggestTimer.current) clearTimeout(suggestTimer.current);
    if (value.trim().length >= 3) {
      suggestTimer.current = setTimeout(async () => {
        setSuggestLoading(true);
        try {
          const res = await api.post('/products/suggest-category', { name: value });
          if (res.data.category_id || res.data.category_name) setSuggestion(res.data);
        } catch {}
        setSuggestLoading(false);
      }, 800);
    }
  }, [updateField]);

  const applySuggestion = useCallback(() => {
    if (!suggestion) return;
    setForm(prev => ({
      ...prev,
      category_id: suggestion.category_id || prev.category_id,
      subcategory_id: suggestion.subcategory_id || ''
    }));
    setSuggestion(null);
    toast.success('Kategori onerisi uygulandi');
  }, [suggestion]);

  // Unit conversions — temiz mantık
  // Form state: {from_unit_id, to_unit_id, factor} — DB ile aynı format
  // UI: "1 [from] = [factor] [to]"
  // Flip: from ↔ to swap, factor = 1/factor (temiz sayı için)
  // Kural: from ve to'dan en az biri "bilinen" (ana birim veya önceki konv.) olmalı

  // Doğrudan bilinen birimler: ana birim + tüm dönüşüm satırlarındaki birimler
  const directKnownIds = [...new Set(
    [form.unit_id, ...form.unit_conversions.flatMap(c => [c.from_unit_id, c.to_unit_id])].filter(Boolean)
  )];

  // Otomatik genişletme sadece ağırlık ve hacim gruplarında (fiziksel dönüşüm mümkün)
  // 'quantity' grubu (adet, porsiyon, kap, kova vb.) birbiriyle alakasız — otomatik genişletme YOK
  const AUTO_EXPAND_GROUPS = ['weight', 'volume'];
  const knownGroups = [...new Set(
    directKnownIds
      .map(id => units.find(u => u.id === id)?.unit_group)
      .filter(g => g && AUTO_EXPAND_GROUPS.includes(g))
  )];
  const knownUnitIds = [...new Set([
    ...directKnownIds,
    ...units.filter(u => knownGroups.includes(u.unit_group)).map(u => u.id)
  ])];

  const addConversion = () => {
    if (form.unit_conversions.length >= 3) { toast.error('Maksimum 3 donusum'); return; }
    if (!form.unit_id) { toast.error('Once ana birim secin'); return; }
    // Varsayılan: 1 [boş] = factor [ana birim]
    setForm(prev => ({
      ...prev,
      unit_conversions: [...prev.unit_conversions, { from_unit_id: '', to_unit_id: form.unit_id, factor: '' }]
    }));
  };

  const flipConversion = (i) => {
    const convs = [...form.unit_conversions];
    const c = convs[i];
    const f = parseFloat(c.factor);
    convs[i] = {
      from_unit_id: c.to_unit_id,
      to_unit_id:   c.from_unit_id,
      factor: f > 0 ? String(Math.round((1/f) * 1000000) / 1000000) : ''
    };
    setForm(prev => ({ ...prev, unit_conversions: convs }));
  };
  const removeConversion = (i) => {
    setForm(prev => ({ ...prev, unit_conversions: prev.unit_conversions.filter((_, j) => j !== i) }));
  };
  const updateConversion = (i, field, value) => {
    const convs = [...form.unit_conversions];
    convs[i] = { ...convs[i], [field]: value };
    setForm(prev => ({ ...prev, unit_conversions: convs }));
  };

  const isSameGroup = (uid1, uid2) => {
    const u1 = units.find(x => x.id === uid1);
    const u2 = units.find(x => x.id === uid2);
    return u1 && u2 && u1.unit_group === u2.unit_group && u1.unit_group !== 'quantity';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { ...form };
      if (payload.min_stock_level === '') payload.min_stock_level = null;
      else payload.min_stock_level = parseFloat(payload.min_stock_level);
      if (!payload.category_id) payload.category_id = null;
      if (!payload.subcategory_id) payload.subcategory_id = null;
      if (!payload.sinif_id) payload.sinif_id = null;
      if (!payload.unit_id) payload.unit_id = null;
      // Bilinen birim IDs (kayıt sırasında yeniden hesapla)
      const savedDirectKnown = new Set([payload.unit_id]);
      const validConversions = [];
      for (const c of payload.unit_conversions) {
        if (!c.from_unit_id || !c.to_unit_id || !c.factor || c.from_unit_id === c.to_unit_id) continue;
        // En az bir taraf bilinen olmalı (aynı grup otomatik dönüşüm dahil)
        const fromUnit = units.find(u => u.id === c.from_unit_id);
        const toUnit   = units.find(u => u.id === c.to_unit_id);
        const knownGroups2 = [...new Set(
          [...savedDirectKnown].map(id => units.find(u => u.id === id)?.unit_group)
            .filter(g => g && ['weight','volume'].includes(g))
        )];
        const fromKnown = savedDirectKnown.has(c.from_unit_id) || (fromUnit && knownGroups2.includes(fromUnit.unit_group));
        const toKnown   = savedDirectKnown.has(c.to_unit_id)   || (toUnit   && knownGroups2.includes(toUnit.unit_group));
        if (!fromKnown && !toKnown) {
          toast.error(`"${fromUnit?.abbreviation}" ve "${toUnit?.abbreviation}" birimleri arasındaki dönüşüm sistemle bağlantılı değil`);
          setLoading(false); return;
        }
        validConversions.push({ from_unit_id: c.from_unit_id, to_unit_id: c.to_unit_id, factor: parseFloat(c.factor) });
        savedDirectKnown.add(c.from_unit_id);
        savedDirectKnown.add(c.to_unit_id);
      }
      payload.unit_conversions = validConversions;

      if (isEdit) {
        await api.patch(`/products/${id}`, payload);
        toast.success('Urun guncellendi');
        // Ürün listesi oturum başında BİR KEZ çekiliyor. Kart güncellendiğinde
        // açık duran fatura ekranı eski değeri kullanmaya devam ediyordu; KDV
        // oranı faturada kilitli gösterildiği için kullanıcı yanlış oranı
        // ekranda görüp değiştiremiyordu. Hem bu sekme hem diğer sekmeler
        // haberdar edilir.
        refreshProducts?.();
        try {
          const channel = new BroadcastChannel('erp_product_updated');
          channel.postMessage({ product_id: id });
          channel.close();
        } catch {}
        duzenlemeBitti(`/erp/products/${id}`);
      } else {
        const res = await api.post('/products', payload);
        toast.success('Urun olusturuldu');
        // Broadcast to other tabs (invoice page)
        try {
          const channel = new BroadcastChannel('erp_product_created');
          channel.postMessage({
            product_id: res.data.id,
            product_name: res.data.name,
            product_code: res.data.code
          });
          channel.close();
        } catch {}
        if (returnTo === 'invoice') {
          setSavedProduct(res.data);
        } else {
          navigate(`/erp/products/${res.data.id}`, { replace: true });
        }
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Hata olustu');
    }
    setLoading(false);
  };

  // UnitSelect rendered inline to avoid remount issues

  if (savedProduct) {
    return (
      <div className="max-w-md mx-auto py-12 text-center" data-testid="product-saved">
        <div className="w-16 h-16 rounded-full bg-[#3d6e4e]/10 flex items-center justify-center mx-auto mb-4">
          <Check size={32} className="text-[#3d6e4e]" />
        </div>
        <h2 className="text-2xl font-bold text-[#1a1916] mb-2">Urun Olusturuldu</h2>
        <p className="text-[#8a7e70] mb-1">{savedProduct.name}</p>
        <p className="text-sm text-[#8a7e70] font-mono mb-6">{savedProduct.code}</p>
        <p className="text-sm text-[#3d6e4e] font-medium mb-6">Fatura sayfasinda otomatik eslesti</p>
        <Button type="button" onClick={() => window.close()} className="bg-[#2e2d29] text-[#f5f0e8] hover:bg-[#1a1916]">
          Bu Sekmeyi Kapat
        </Button>
      </div>
    );
  }

  return (
    <div data-testid="product-form-page">
      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" onClick={geri} className="text-[#8a7e70] hover:text-[#1a1916]" data-testid="back-btn">
          <ArrowLeft size={18} />
        </Button>
        <h1 className="text-3xl font-bold text-[#1a1916] tracking-tight">
          {isEdit ? 'Urun Duzenle' : 'Yeni Urun Karti'}
        </h1>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <Card className="bg-[#faf7f2] border-[#d0c4b0] shadow-sm">
          <CardHeader className="border-b border-[#d0c4b0]">
            <CardTitle className="text-lg font-bold text-[#1a1916]">Urun Bilgileri</CardTitle>
          </CardHeader>
          <CardContent className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label className="text-[#3F3E39] font-medium">Urun Kodu *</Label>
              <div className="flex gap-2">
                <Input value={form.code} onChange={(e) => handleCodeChange(e.target.value)}
                  readOnly={!isEdit && kodKilitli}
                  className={`border-[#d0c4b0] font-mono ${!isEdit && kodKilitli
                    ? 'bg-[#f0ebe1] text-[#3F3E39] cursor-default'
                    : 'bg-white'}`}
                  required data-testid="product-code-input" />
                {!isEdit && (kodKilitli ? (
                  <Button type="button" variant="outline" size="sm"
                    onClick={() => setKodKilitli(false)}
                    className="border-[#d0c4b0] text-[#3F3E39] shrink-0"
                    data-testid="kod-degistir">
                    <Pencil size={13} className="mr-1" /> Değiştir
                  </Button>
                ) : (
                  <Button type="button" variant="outline" size="sm"
                    onClick={() => {
                      // Otomatik koda dön: öneriyi geri yaz ve kilidi kapat
                      if (kodBilgi?.suggested_code) updateField('code', kodBilgi.suggested_code);
                      setCodeSuggested(true);
                      setKodKilitli(true);
                    }}
                    className="border-[#d0c4b0] text-[#3d6e4e] shrink-0"
                    data-testid="kod-otomatik">
                    <Sparkles size={13} className="mr-1" /> Otomatik
                  </Button>
                ))}
              </div>
              {/* Kod alanının altı: öneri rozeti + TÜRÜN son kodu. Kullanıcı yeni
                  ürün açarken "en son hangi kod verilmişti"yi göremiyordu. */}
              {!isEdit && kodBilgi && (
                <div className="text-xs space-y-0.5" data-testid="kod-bilgi">
                  {codeSuggested && (
                    <p className="text-[#3d6e4e] flex items-center gap-1">
                      <Sparkles size={10} /> Sıradaki boş kod otomatik yazıldı
                    </p>
                  )}
                  <p className="text-[#8a7e70]">
                    <b>{types.find(t => t.value === form.type)?.label || form.type}</b>
                    {kodBilgi.son_kod ? (
                      <>
                        {' '}türünde son kod{' '}
                        <span className="font-mono text-[#1a1916]">{kodBilgi.son_kod}</span>
                        {kodBilgi.son_urun ? ` — ${kodBilgi.son_urun}` : ''}
                        {kodBilgi.aile_adet ? ` (${kodBilgi.aile_adet} ürün)` : ''}
                      </>
                    ) : (
                      <>
                        {' '}türünde ilk kod —{' '}
                        <span className="font-mono">{kodBilgi.onek}-</span> ile başlıyor
                      </>
                    )}
                  </p>
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label className="text-[#3F3E39] font-medium">Urun Adi *</Label>
              <Input value={form.name} onChange={(e) => handleNameChange(e.target.value)}
                className="bg-white border-[#d0c4b0]" required data-testid="product-name-input" />
              {suggestLoading && (
                <p className="text-xs text-[#8a7e70] flex items-center gap-1.5 animate-pulse">
                  <Sparkles size={12} /> Kategori onerisi aliniyor...
                </p>
              )}
              {suggestion && (suggestion.category_name || suggestion.subcategory_name) && (
                <div className="flex items-center gap-2 p-2 rounded-md bg-[#3d6e4e]/8 border border-[#3d6e4e]/20" data-testid="ai-suggestion">
                  <Sparkles size={14} className="text-[#3d6e4e] flex-shrink-0" />
                  <span className="text-sm text-[#3F3E39] flex-1">
                    Oneri: <span className="font-semibold text-[#1a1916]">
                      {suggestion.category_name}{suggestion.subcategory_name ? ` \u203A ${suggestion.subcategory_name}` : ''}
                    </span>
                  </span>
                  <Button type="button" size="sm" onClick={applySuggestion}
                    className="h-7 px-2.5 bg-[#3d6e4e] text-white hover:bg-[#2d5e3e] text-xs font-semibold"
                    data-testid="apply-suggestion-btn">
                    <Check size={12} className="mr-1" /> Uygula
                  </Button>
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label className="text-[#3F3E39] font-medium">Kategori</Label>
              <Select key={`cat-${categories.length}-${form.category_id}`} value={form.category_id || undefined} onValueChange={(v) => updateField('category_id', v === 'none' ? '' : v)}>
                <SelectTrigger className="bg-white border-[#d0c4b0]" data-testid="product-category-select">
                  <SelectValue placeholder="Kategori secin" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Kategori yok</SelectItem>
                  {parentCategories.map(c => <SelectItem key={c.id} value={c.id}>{katAdi(c)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-[#3F3E39] font-medium">Alt Kategori</Label>
              <Select key={`subcat-${categories.length}-${form.category_id}-${form.subcategory_id}`} value={form.subcategory_id || undefined}
                onValueChange={(v) => updateField('subcategory_id', v === 'none' ? '' : v)} disabled={!form.category_id}>
                <SelectTrigger className={`bg-white border-[#d0c4b0] ${!form.category_id ? 'opacity-50' : ''}`} data-testid="product-subcategory-select">
                  <SelectValue placeholder={!form.category_id ? 'Once kategori secin' : 'Alt kategori secin'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Alt kategori yok</SelectItem>
                  {subcategories.map(c => <SelectItem key={c.id} value={c.id}>{katAdi(c)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {(siniflar.length > 0 || form.sinif_id) && (
              <div className="space-y-2">
                <Label className="text-[#3F3E39] font-medium">Masraf Grubu</Label>
                <Select key={`sinif-${categories.length}-${form.subcategory_id}-${form.sinif_id}`} value={form.sinif_id || undefined}
                  onValueChange={(v) => updateField('sinif_id', v === 'none' ? '' : v)}>
                  <SelectTrigger className="bg-white border-[#d0c4b0]" data-testid="product-sinif-select">
                    <SelectValue placeholder="Masraf grubu secin" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Masraf grubu yok</SelectItem>
                    {siniflar.map(c => <SelectItem key={c.id} value={c.id}>{katAdi(c)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-2">
              <Label className="text-[#3F3E39] font-medium">Tur *</Label>
              <Select value={form.type} onValueChange={(v) => updateField('type', v)}>
                <SelectTrigger className="bg-white border-[#d0c4b0]" data-testid="product-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {types.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-[#3F3E39] font-medium">Ana Birim *</Label>
              <Select key={`unit-${units.length}-${form.unit_id}`} value={form.unit_id || undefined} onValueChange={(v) => updateField('unit_id', v)}>
                <SelectTrigger className="bg-white border-[#d0c4b0]" data-testid="product-unit-select">
                  <SelectValue placeholder="Birim secin" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(unitsByGroup).map(([group, groupUnits]) => (
                    <SelectGroup key={group}>
                      <SelectLabel className="text-xs font-bold uppercase text-[#8a7e70]">{groupLabels[group] || group}</SelectLabel>
                      {groupUnits.map(u => (
                        <SelectItem key={u.id} value={u.id}>{u.abbreviation} — {u.name}</SelectItem>
                      ))}
                    </SelectGroup>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-[#3F3E39] font-medium">Barkod</Label>
              <Input value={form.barcode} onChange={(e) => updateField('barcode', e.target.value)}
                className="bg-white border-[#d0c4b0]" data-testid="product-barcode-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-[#3F3E39] font-medium">Minimum Stok Seviyesi</Label>
              <Input type="number" step="0.01" value={form.min_stock_level}
                onChange={(e) => updateField('min_stock_level', e.target.value)}
                className="bg-white border-[#d0c4b0]" data-testid="product-min-stock-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-[#3F3E39] font-medium">KDV Oranı (%)</Label>
              <select value={form.kdv_rate ?? ''} onChange={(e) => updateField('kdv_rate', e.target.value === '' ? '' : Number(e.target.value))}
                className="w-full h-10 rounded-md border border-[#d0c4b0] bg-white text-sm px-3 text-[#1a1916] focus:outline-none"
                data-testid="product-kdv-rate">
                <option value="">Belirtilmemiş</option>
                {[0,5,10,16,20].map(r => <option key={r} value={r}>%{r}</option>)}
              </select>
            </div>
            {/* İnşaatta poz tarifi (ürün kodu = poz no); restoranda isteğe bağlı not */}
            <div className="space-y-2 md:col-span-2">
              <Label className="text-[#3F3E39] font-medium">Açıklama</Label>
              <textarea value={form.description} onChange={(e) => updateField('description', e.target.value)}
                rows={3} placeholder="Örn. poz tarifi: işin tanımı, ölçü ve kabul şartları"
                className="w-full rounded-md border border-[#d0c4b0] bg-white text-sm px-3 py-2 text-[#1a1916] focus:outline-none"
                data-testid="product-description-input" />
            </div>
          </CardContent>
        </Card>

        {/* Unit Conversions */}
        <Card className="bg-[#faf7f2] border-[#d0c4b0] shadow-sm">
          <CardHeader className="border-b border-[#d0c4b0] flex flex-row items-center justify-between">
            <CardTitle className="text-lg font-bold text-[#1a1916]">Alternatif Birim Donusumleri</CardTitle>
            <Button type="button" onClick={addConversion} size="sm" disabled={form.unit_conversions.length >= 3}
              className="bg-[#2e2d29] text-[#f5f0e8] hover:bg-[#1a1916]" data-testid="add-conversion-btn">
              <Plus size={14} className="mr-1" /> Donusum Ekle
            </Button>
          </CardHeader>
          <CardContent className="p-6">
            {form.unit_conversions.length === 0 ? (
              <p className="text-center py-4 text-sm text-[#8a7e70]">Henuz donusum tanimlanmadi. Maksimum 3 adet eklenebilir.</p>
            ) : (
              <div className="space-y-3">
                {form.unit_conversions.map((conv, i) => {
                  const fromUnit = units.find(u => u.id === conv.from_unit_id);
                  const toUnit   = units.find(u => u.id === conv.to_unit_id);
                  const factor   = parseFloat(conv.factor);

                  // Bağlı birimler (görsel işaret için): ana birim + diğer satırlar + w/v grubu otomatik
                  const directConnected = new Set([
                    form.unit_id,
                    ...form.unit_conversions.filter((_, j) => j !== i).flatMap(c => [c.from_unit_id, c.to_unit_id])
                  ].filter(Boolean));
                  const connectedGroups = [...directConnected].map(id => units.find(u => u.id === id)?.unit_group)
                    .filter(g => g && ['weight','volume'].includes(g));
                  const connectedIds = new Set([
                    ...directConnected,
                    ...units.filter(u => connectedGroups.includes(u.unit_group)).map(u => u.id)
                  ]);

                  const reversePreview = factor > 0 && fromUnit && toUnit
                    ? `(1 ${toUnit.abbreviation} = ${(1/factor).toLocaleString('tr-TR',{maximumFractionDigits:6})} ${fromUnit.abbreviation})`
                    : '';

                  return (
                    <div key={i} className="space-y-1.5 p-3 bg-[#f5f0e8]/50 rounded-lg border border-[#e8dfd0]" data-testid={`conversion-row-${i}`}>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-semibold text-[#1a1916]">1</span>

                        <div className="w-44">
                          <Select value={conv.from_unit_id || undefined} onValueChange={(v) => updateConversion(i, 'from_unit_id', v)}>
                            <SelectTrigger className={`h-9 border-[#d0c4b0] ${connectedIds.has(conv.from_unit_id) ? 'bg-[#e8dfd0] font-semibold' : 'bg-white'}`} data-testid={`conv-from-${i}`}>
                              <SelectValue placeholder="Birim seçin" />
                            </SelectTrigger>
                            <SelectContent>
                              {Object.entries(unitsByGroup).map(([group, gu]) => (
                                <SelectGroup key={group}>
                                  <SelectLabel className="text-xs font-bold uppercase text-[#8a7e70]">{groupLabels[group] || group}</SelectLabel>
                                  {gu.filter(u => u.id !== conv.to_unit_id).map(u => (
                                    <SelectItem key={u.id} value={u.id}>{connectedIds.has(u.id) ? '✓ ' : '  '}{u.abbreviation}{' — '}{u.name}</SelectItem>
                                  ))}
                                </SelectGroup>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        <span className="text-sm text-[#8a7e70]">=</span>

                        <div className="w-28">
                          <Input type="number" step="0.0001" min="0.0001" value={conv.factor}
                            onChange={(e) => updateConversion(i, 'factor', e.target.value)}
                            className="bg-white border-[#d0c4b0] text-center h-9"
                            placeholder="örn. 0,09"
                            data-testid={`conv-factor-${i}`} />
                        </div>

                        <div className="w-44">
                          <Select value={conv.to_unit_id || undefined} onValueChange={(v) => updateConversion(i, 'to_unit_id', v)}>
                            <SelectTrigger className={`h-9 border-[#d0c4b0] ${connectedIds.has(conv.to_unit_id) ? 'bg-[#e8dfd0] font-semibold' : 'bg-white'}`} data-testid={`conv-to-${i}`}>
                              <SelectValue placeholder="Birim seçin" />
                            </SelectTrigger>
                            <SelectContent>
                              {Object.entries(unitsByGroup).map(([group, gu]) => (
                                <SelectGroup key={group}>
                                  <SelectLabel className="text-xs font-bold uppercase text-[#8a7e70]">{groupLabels[group] || group}</SelectLabel>
                                  {gu.filter(u => u.id !== conv.from_unit_id).map(u => (
                                    <SelectItem key={u.id} value={u.id}>{connectedIds.has(u.id) ? '✓ ' : '  '}{u.abbreviation}{' — '}{u.name}</SelectItem>
                                  ))}
                                </SelectGroup>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        <Button type="button" variant="outline" size="icon" onClick={() => flipConversion(i)}
                          className="h-9 w-9 border-[#d0c4b0] text-[#8a7e70] hover:text-[#1a1916] hover:bg-[#e8dfd0]"
                          title="Yönü çevir — factor tersine döner">
                          <ArrowLeftRight size={14} />
                        </Button>

                        <Button type="button" variant="ghost" size="icon" onClick={() => removeConversion(i)}
                          className="h-9 w-9 text-[#8a7e70] hover:text-[#7a3535]" data-testid={`conv-remove-${i}`}>
                          <X size={16} />
                        </Button>
                      </div>

                      {reversePreview && (
                        <p className="text-xs text-[#8a7e70] pl-1">&#8596; {reversePreview}</p>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        <div className="flex justify-end gap-3">
          <Button type="button" variant="outline" onClick={geri}
            className="border-[#d0c4b0] text-[#3F3E39] hover:bg-[#e8dfd0]" data-testid="cancel-btn">Iptal</Button>
          <Button type="submit" disabled={loading}
            className="bg-[#2e2d29] text-[#f5f0e8] hover:bg-[#1a1916] font-semibold" data-testid="save-product-btn">
            <Save size={16} className="mr-2" /> {loading ? 'Kaydediliyor...' : 'Kaydet'}
          </Button>
        </div>
      </form>
    </div>
  );
}
